// Night torch/campfire glows — additive point sprites at city and unit
// positions whose opacity tracks the day/night cycle (dayNightState).
//
// Two Points systems (one draw call each): bigger warm glows at cities,
// small torch glows on units. A slow global flicker breathes on the material
// opacity, so the map feels lit by fire rather than a static overlay.
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { dayNightState, nightFactor } from './DayNightLighting.js';

function glowTexture(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,190,110,0.9)');
  g.addColorStop(0.35, 'rgba(255,140,60,0.45)');
  g.addColorStop(1, 'rgba(255,120,40,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

interface NightLightsProps {
  /** City anchor spots (x/z world, y surface) — campfire-scale glows. */
  readonly cities: ReadonlyArray<{ x: number; y: number; z: number }>;
  /** Unit anchors — torch-scale glows. */
  readonly units: ReadonlyArray<{ x: number; y: number; z: number }>;
}

const CITY_SIZE = 1.6;
const UNIT_SIZE = 0.5;

export function NightLights({ cities, units }: NightLightsProps) {
  const cityRef = useRef<THREE.Points>(null);
  const unitRef = useRef<THREE.Points>(null);
  const tex = useMemo(() => glowTexture(), []);

  const makeGeo = (spots: NightLightsProps['cities']) => {
    const positions = new Float32Array(spots.length * 3);
    spots.forEach((s, i) => {
      positions[i * 3] = s.x;
      positions[i * 3 + 1] = s.y + 0.35;
      positions[i * 3 + 2] = s.z;
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geo;
  };

  const cityGeo = useMemo(() => makeGeo(cities), [cities]);
  const unitGeo = useMemo(() => makeGeo(units), [units]);

  // Materials are created once; opacity is driven per frame.
  const cityMat = useMemo(
    () =>
      new THREE.PointsMaterial({
        map: tex,
        color: 0xffc080,
        size: CITY_SIZE,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    [tex],
  );
  const unitMat = useMemo(
    () =>
      new THREE.PointsMaterial({
        map: tex,
        color: 0xffb060,
        size: UNIT_SIZE,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    [tex],
  );

  useEffect(
    () => () => {
      cityGeo.dispose();
      unitGeo.dispose();
      cityMat.dispose();
      unitMat.dispose();
      tex.dispose();
    },
    [cityGeo, unitGeo, cityMat, unitMat, tex],
  );

  useFrame(({ clock }) => {
    const night = nightFactor(dayNightState.sunHeight);
    // Slow fire-breath: ±12% around the base, invisible in daylight anyway.
    const flicker = 1 + Math.sin(clock.elapsedTime * 2.3) * 0.12;
    if (cityRef.current) {
      cityRef.current.visible = night > 0.01 && cities.length > 0;
      cityMat.opacity = night * 0.85 * flicker;
    }
    if (unitRef.current) {
      unitRef.current.visible = night > 0.01 && units.length > 0;
      unitMat.opacity = night * 0.55 * flicker;
    }
  });

  if (cities.length === 0 && units.length === 0) return null;
  return (
    <>
      {cities.length > 0 && (
        <points ref={cityRef} geometry={cityGeo} material={cityMat} frustumCulled={false} />
      )}
      {units.length > 0 && (
        <points ref={unitRef} geometry={unitGeo} material={unitMat} frustumCulled={false} />
      )}
    </>
  );
}
